import { useState, useEffect, useCallback, useRef } from 'react';
import supabase from '../config/supabaseConfig';
import { Ad } from '../types/adType';

interface UseGetAdsProps {
    categoryId?: string;
    subcategoryId?: string;
    searchQuery?: string;
    stateId?: string;
    cityId?: string;
    sort?: string;
    pageSize?: number;
    maxRetries?: number;
}

const DEFAULT_MAX_RETRIES = 3;

export const useGetAds = ({
    categoryId,
    subcategoryId,
    searchQuery,
    stateId,
    cityId,
    sort,
    pageSize = 12,
    maxRetries = DEFAULT_MAX_RETRIES
}: UseGetAdsProps) => {
    const [ads, setAds] = useState<Ad[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [fetchFailed, setFetchFailed] = useState(false);

    // Track fetch attempts for each query. Scoped to this hook instance (via
    // useRef) rather than module scope, so a past failure doesn't permanently
    // block every future mount of this hook from even attempting a fetch —
    // it only caps retries within the current mount's lifetime.
    const fetchAttemptsMapRef = useRef(new Map<string, number>());

    // Create a request ID to track retry attempts
    const requestId = useCallback(() => {
        return `ads_${categoryId || ''}_${subcategoryId || ''}_${searchQuery || ''}_${stateId || ''}_${cityId || ''}_${sort || ''}_${currentPage}`;
    }, [categoryId, subcategoryId, searchQuery, stateId, cityId, sort, currentPage]);

    const fetchAds = useCallback(async (page: number) => {
        const currentRequestId = requestId();

        // Check if we've exceeded max retries
        const attempts = fetchAttemptsMapRef.current.get(currentRequestId) || 0;
        if (attempts >= maxRetries) {
            setFetchFailed(true);
            setLoading(false);
            return;
        }

        // Increment attempt counter
        fetchAttemptsMapRef.current.set(currentRequestId, attempts + 1);

        try {
            setLoading(true);
            setError(null);

            // Build the query
            let query = supabase
                .from('ads')
                .select(`
                    *,
                    categories:category_id(name),
                    subcategories:subcategory_id(name),
                    states:state_id(name),
                    cities:city_id(name),
                    profiles:user_id(id, name, email, is_verified, photo_url),
                    product_images(url)
                `, { count: 'exact' })
                .eq('is_deleted', false)
                .eq('is_sold', false);

            // Apply filters
            if (categoryId) {
                query = query.eq('category_id', categoryId);
            }
            if (subcategoryId) {
                query = query.eq('subcategory_id', subcategoryId);
            }
            if (stateId) {
                query = query.eq('state_id', stateId);
            }
            if (cityId) {
                query = query.eq('city_id', cityId);
            }
            if (searchQuery) {
                query = query.ilike('title', `%${searchQuery}%`);
            }

            const orderColumn = sort === 'high' || sort === 'low' ? 'price' : 'published_at';
            query = query.order(orderColumn, { ascending: sort === 'low' });

            // Add pagination
            const from = (page - 1) * pageSize;
            const to = from + pageSize - 1;
            query = query.range(from, to);

            // Execute query
            const { data, error: fetchError, count } = await query;

            if (fetchError) throw fetchError;

            // Transform the data to match the Ad type
            const transformedAds = data.map(ad => ({
                ...ad,
                category_name: ad.categories?.name,
                subcategory_name: ad.subcategories?.name,
                state_name: ad.states?.name,
                city_name: ad.cities?.name,
            })) as Ad[];

            setTotalCount(count || 0);
            setHasMore(transformedAds.length === pageSize);

            if (page === 1) {
                setAds(transformedAds);
            } else {
                setAds(prev => [...prev, ...transformedAds]);
            }

            // Reset fetch attempts on success
            fetchAttemptsMapRef.current.delete(currentRequestId);
            setFetchFailed(false);
        } catch (err) {
            console.error('[useGetAds] fetch failed:', err);
            setError(err instanceof Error ? err.message : 'Échec de la récupération des annonces');

            // Check if we've hit max retries after this attempt
            const currentAttempts = fetchAttemptsMapRef.current.get(currentRequestId) || 0;
            if (currentAttempts >= maxRetries) {
                setFetchFailed(true);
            }
        } finally {
            setLoading(false);
        }
    }, [categoryId, subcategoryId, searchQuery, stateId, cityId, sort, pageSize, maxRetries, requestId]);

    // Initial fetch
    useEffect(() => {
        // If already failed, don't try again
        if (fetchFailed) return;

        setCurrentPage(1);
        fetchAds(1);
    }, [fetchAds, fetchFailed]);

    // Load more function
    const loadMore = useCallback(() => {
        if (!loading && hasMore && !fetchFailed) {
            setCurrentPage(prev => prev + 1);
            fetchAds(currentPage + 1);
        }
    }, [loading, hasMore, currentPage, fetchAds, fetchFailed]);

    // Reset retry counter to allow retrying after failure
    const retryFetch = useCallback(() => {
        const currentRequestId = requestId();
        fetchAttemptsMapRef.current.delete(currentRequestId);
        setFetchFailed(false);
        setCurrentPage(1);
        fetchAds(1);
    }, [fetchAds, requestId]);

    return {
        ads,
        loading,
        error,
        currentPage,
        totalCount,
        hasMore,
        loadMore,
        pageSize,
        fetchFailed,
        retryFetch
    };
};
